/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { storyblokEditable } from '@storyblok/react/rsc'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useState } from 'react'

import { CaseStudyCard } from '@/components/storyblok/components/kits/CaseStudyCard'
import { RichTextRenderer } from '@/components/storyblok/components/ui/RichtextRenderer'
import { CaseStudyListStoryblok } from '@/lib/storyblok/types/generated-types'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type CaseStudyListProps = {
  /** The Storyblok blok that represents this component. */
  blok: CaseStudyListStoryblok
}

/**
 * A Storyblok component for displaying a list of case studies. This component includes a title and a list of case
 * studies that are displayed as cards.
 */
const CaseStudyList: React.FC<CaseStudyListProps> = ({ blok }) => {
  const t = useTranslations('CaseStudyList')
  const { case_studies, title } = blok
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: case_studies.length > 3,
  })

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<Array<number>>([])
  const [isScrollable, setIsScrollable] = useState(false)

  const onDotClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi],
  )

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap())
    setIsScrollable(api.canScrollPrev() || api.canScrollNext())
  }, [])

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('reInit', (api) => {
      onSelect(api)
      setScrollSnaps(api.scrollSnapList())
    })
    emblaApi.on('select', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  return (
    <div className={'flex flex-col gap-2'} {...storyblokEditable(blok)}>
      <RichTextRenderer document={title} />
      <div className={cn('relative', isScrollable && 'px-12')}>
        <div className='overflow-hidden' ref={emblaRef}>
          <div className='flex -ml-5'>
            {case_studies.map((study) => (
              <div className='relative min-w-0 shrink-0 w-full md:w-1/2 lg:w-1/3 pl-5' key={study._uid}>
                <CaseStudyCard {...study} {...storyblokEditable(study)} />
              </div>
            ))}
          </div>
        </div>
        {isScrollable && (
          <>
            <button
              onClick={scrollPrev}
              aria-label={t('previousSlide')}
              className={
                'absolute top-1/2 left-0 transform -translate-y-1/2 bg-white rounded-full shadow-lg p-2 z-10 cursor-pointer'
              }
            >
              <ChevronLeft className={'h-4 w-4 text-gray-600'} />
            </button>
            <button
              onClick={scrollNext}
              aria-label={t('nextSlide')}
              className={
                'absolute top-1/2 right-0 transform -translate-y-1/2 bg-white rounded-full shadow-lg p-2 z-10 cursor-pointer'
              }
            >
              <ChevronRight className={'h-4 w-4 text-gray-600'} />
            </button>
            <div className='flex justify-center items-center gap-3 mt-4'>
              {scrollSnaps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => onDotClick(index)}
                  className={cn(
                    'w-2 h-2 rounded-full transition-all duration-300',
                    index === selectedIndex ? 'bg-blue-600' : 'bg-slate-300',
                  )}
                  aria-label={t('goToSlide', { index: index + 1 })}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export { CaseStudyList }
